/**
 * Rückfrage vor einer neuen Kandidatensuche: Das Autosave startet ab Schritt 4 bei geänderter Suchsignatur sofort eine
 * neue Suche und verwirft die bisherigen Kandidaten (Backend `wizard-state.service`). Rein und testbar.
 */

import type { WizardCandidateList, WizardState } from './campaign-wizard.types';

/** Felder, die die Kandidatensuche bestimmen (Backend: Suchsignatur). */
export const SEARCH_RELEVANT_KEYS = [
  'jobTitle',
  'method',
  'locations',
  'criteria',
  'searchUrl',
  'searchUrls',
] as const;

/** Ändert der Patch mindestens ein suchrelevantes Feld gegenüber dem Stand? (Gleiche Werte zählen nicht.) */
export const isSearchRelevantPatch = (
  current: Pick<WizardState, (typeof SEARCH_RELEVANT_KEYS)[number]>,
  incoming: Partial<WizardState>
): boolean =>
  SEARCH_RELEVANT_KEYS.some(
    key =>
      incoming[key] !== undefined && JSON.stringify(incoming[key]) !== JSON.stringify(current[key])
  );

/**
 * Gibt es eine Suche, deren Kandidaten eine Änderung verwerfen würde? Die Auskunft kommt vom Server (`hasSearch` im
 * Snapshot: laufende oder fertige Suche, nicht fehlgeschlagen). Ist sie noch nicht geladen (`undefined`), wird lieber
 * gefragt. Ohne angelegte Kampagne gibt es nie eine Suche.
 */
export const searchAlreadyRan = (hasSearch: boolean | undefined, campaignId: string | null): boolean =>
  !!campaignId && hasSearch !== false;

/** Die Bestätigung gilt für genau eine Bearbeitung: dieselbe Kampagne und derselbe Schritt, in dem bestätigt wurde. */
export interface SearchConfirmation {
  campaignId: string;
  step: number;
}

export const isSearchConfirmed = (
  confirmation: SearchConfirmation | null,
  campaignId: string | null,
  step: number
): boolean => !!confirmation && !!campaignId && confirmation.campaignId === campaignId && confirmation.step === step;

/** Muss der Nutzer bestätigen, bevor der Patch angewendet wird? Nicht, wenn er in dieser Bearbeitung schon bestätigt hat. */
export const needsSearchRestartConfirm = (
  state: WizardState,
  campaignId: string | null,
  hasSearch: boolean | undefined,
  incoming: Partial<WizardState>,
  alreadyConfirmed: boolean
): boolean =>
  !alreadyConfirmed && searchAlreadyRan(hasSearch, campaignId) && isSearchRelevantPatch(state, incoming);

/**
 * Zeigt die Kandidatenliste (Schritt 4), dass es eine Suche mit (künftigen) Kandidaten gibt? Laufende oder fertige Suche,
 * oder schon geladene Kandidaten (auch nach einer fehlgeschlagenen Suche). So kennt der Assistent die Suche sofort,
 * z. B. nach „Suche erneut starten“, und nicht erst nach dem nächsten Speichern.
 */
export const listShowsSearch = (
  list: Pick<WizardCandidateList, 'searchStatus' | 'total' | 'importing'>
): boolean =>
  list.searchStatus === 'running' || list.searchStatus === 'finished' || list.importing || list.total > 0;

/** Name der Kopie: „X (Kopie)“, bei Namenskollision „X (Kopie 2)“, „X (Kopie 3)“ … */
export const duplicateName = (name: string, suffix: string, attempt: number): string =>
  `${name.trim()} (${suffix}${attempt > 1 ? ` ${attempt}` : ''})`;
